/**
 * panels.js — in-world canvas-texture panels.
 *
 * Every diegetic readout (stall scoreboard, payout summary, prompts, the VR
 * wallet) is one of these: a plane with a canvas texture that can be redrawn in
 * place. Redrawing costs one texture upload, which is cheap enough to do on
 * every score event but not every frame.
 *
 * A stall gets a live scoreboard with:
 *
 *   const board = createPanel({ title: 'TIN CAN ALLEY', width: 1.6, height: 0.9 });
 *   board.setLines(['Score  120', 'Balls  2 / 3']);
 *   stallRoot.add(board);
 *
 * @module ui/panels
 */

import * as THREE from 'three';
import { PALETTE } from '../constants.js';

const PX_PER_METRE = 320;

/**
 * @typedef {Object} PanelOptions
 * @property {string} [title]
 * @property {string[]} [lines]
 * @property {number} [width=1.4] Metres.
 * @property {number} [height=0.8] Metres.
 * @property {string} [bg='#141a2e']
 * @property {string} [titleColor='#ffd166']
 * @property {string} [textColor='#e8eefc']
 * @property {string} [accent='rgba(255,209,102,0.55)'] Border colour.
 * @property {number} [radius=26] Corner radius in pixels.
 * @property {boolean} [doubleSided=false]
 * @property {'left'|'center'} [align='center']
 */

/**
 * Build a panel.
 * @param {PanelOptions} [opts]
 * @returns {THREE.Mesh} With setTitle/setLines/setHighlight/dispose on userData
 *   and mirrored as direct methods for convenience.
 */
export function createPanel(opts = {}) {
  const width = opts.width ?? 1.4;
  const height = opts.height ?? 0.8;
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(width * PX_PER_METRE);
  canvas.height = Math.round(height * PX_PER_METRE);
  const ctx = canvas.getContext('2d');

  const state = {
    title: opts.title ?? '',
    lines: opts.lines ?? [],
    highlight: null,
    bg: opts.bg ?? '#141a2e',
    titleColor: opts.titleColor ?? PALETTE.signText,
    textColor: opts.textColor ?? '#e8eefc',
    accent: opts.accent ?? 'rgba(255,209,102,0.55)',
    radius: opts.radius ?? 26,
    align: opts.align ?? 'center',
  };

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.anisotropy = 4;

  const material = new THREE.MeshBasicMaterial({
    map: texture,
    transparent: true,
    side: opts.doubleSided ? THREE.DoubleSide : THREE.FrontSide,
    depthWrite: false,
  });
  const panel = new THREE.Mesh(new THREE.PlaneGeometry(width, height), material);
  panel.name = 'panel';

  function draw() {
    const w = canvas.width;
    const h = canvas.height;
    ctx.clearRect(0, 0, w, h);

    ctx.fillStyle = state.bg;
    roundRect(ctx, 0, 0, w, h, state.radius);
    ctx.fill();

    if (state.accent) {
      ctx.strokeStyle = state.accent;
      ctx.lineWidth = 5;
      roundRect(ctx, 3, 3, w - 6, h - 6, state.radius);
      ctx.stroke();
    }

    const pad = 26;
    let y = pad;
    const centred = state.align === 'center';
    const x = centred ? w / 2 : pad;
    ctx.textAlign = centred ? 'center' : 'left';
    ctx.textBaseline = 'top';

    if (state.title) {
      const titleSize = Math.round(h * 0.15);
      ctx.font = `bold ${titleSize}px "Trebuchet MS", sans-serif`;
      ctx.fillStyle = state.titleColor;
      ctx.fillText(state.title, x, y);
      y += titleSize * 1.35;

      ctx.strokeStyle = 'rgba(255,255,255,0.14)';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(pad, y);
      ctx.lineTo(w - pad, y);
      ctx.stroke();
      y += 14;
    }

    const remaining = h - y - pad;
    const count = Math.max(1, state.lines.length);
    const lineSize = Math.min(Math.round(h * 0.16), Math.floor(remaining / count) - 6);
    ctx.font = `bold ${lineSize}px "Trebuchet MS", sans-serif`;

    for (let i = 0; i < state.lines.length; i++) {
      const line = state.lines[i];
      ctx.fillStyle = (state.highlight === i) ? state.titleColor : state.textColor;
      ctx.fillText(String(line), x, y);
      y += lineSize * 1.3;
    }

    texture.needsUpdate = true;
  }

  draw();

  /** @param {string} title */
  panel.setTitle = (title) => { state.title = title; draw(); };
  /** @param {string[]|string} lines */
  panel.setLines = (lines) => { state.lines = Array.isArray(lines) ? lines : [lines]; draw(); };
  /** @param {number|null} index Line index to render in the accent colour. */
  panel.setHighlight = (index) => { state.highlight = index; draw(); };
  /** @param {Partial<PanelOptions>} patch */
  panel.setStyle = (patch) => { Object.assign(state, patch); draw(); };
  panel.redraw = draw;
  panel.dispose = () => {
    texture.dispose();
    material.dispose();
    panel.geometry.dispose();
  };

  panel.userData = {
    setTitle: panel.setTitle,
    setLines: panel.setLines,
    setHighlight: panel.setHighlight,
    canvas,
    ctx,
    texture,
  };
  return panel;
}

/**
 * A floating score popup that rises and fades, then removes itself.
 * @param {string|number} text
 * @param {{ color?: string, size?: number, duration?: number, rise?: number }} [opts]
 * @returns {THREE.Sprite} With `userData.update(dt)` returning false when done.
 */
export function createScorePopup(text, opts = {}) {
  const color = opts.color ?? '#ffd166';
  const duration = opts.duration ?? 1.1;
  const rise = opts.rise ?? 0.55;

  const canvas = document.createElement('canvas');
  canvas.width = 256;
  canvas.height = 128;
  const ctx = canvas.getContext('2d');
  ctx.font = 'bold 76px "Trebuchet MS", sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.lineWidth = 8;
  ctx.strokeStyle = 'rgba(0,0,0,0.65)';
  ctx.strokeText(String(text), 128, 64);
  ctx.fillStyle = color;
  ctx.fillText(String(text), 128, 64);

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  const material = new THREE.SpriteMaterial({ map: texture, transparent: true, depthTest: false });
  const sprite = new THREE.Sprite(material);
  const scale = opts.size ?? 0.42;
  sprite.scale.set(scale * 2, scale, 1);
  sprite.renderOrder = 9000;

  let t = 0;
  const startY = 0;
  /**
   * @param {number} dt
   * @returns {boolean} False once the popup has finished and should be removed.
   */
  sprite.userData.update = (dt) => {
    t += dt;
    const k = t / duration;
    if (k >= 1) {
      material.dispose();
      texture.dispose();
      return false;
    }
    sprite.position.y = startY + rise * easeOut(k);
    material.opacity = k < 0.7 ? 1 : 1 - (k - 0.7) / 0.3;
    const pop = k < 0.16 ? 0.7 + (k / 0.16) * 0.4 : 1.05 - k * 0.05;
    sprite.scale.set(scale * 2 * pop, scale * pop, 1);
    return true;
  };
  return sprite;
}

function easeOut(k) {
  return 1 - (1 - k) * (1 - k);
}

function roundRect(ctx, x, y, w, h, r) {
  const rad = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rad, y);
  ctx.arcTo(x + w, y, x + w, y + h, rad);
  ctx.arcTo(x + w, y + h, x, y + h, rad);
  ctx.arcTo(x, y + h, x, y, rad);
  ctx.arcTo(x, y, x + w, y, rad);
  ctx.closePath();
}
